import isEqual from 'lodash/isEqual'
import { IntlShape } from 'react-intl'

import { SearchCityDatesAdultsModeEnum } from './search-city-dates-adults.enum'
import { Inputs } from './useFieldNavigator'
import { budgetMap } from '../../forms/budget-picker/constants'
import { TravelTypeEnum } from '../../../lib/graphql'
import {
  capitalizeFirstLetter,
  DateFormatter,
  getFormatter,
} from '../../../utils'

export const getSearchCityDatesAdultClassNameByEnum = (
  searchCityDatesAdultsModeEnum: SearchCityDatesAdultsModeEnum,
): string => {
  switch (searchCityDatesAdultsModeEnum) {
    case SearchCityDatesAdultsModeEnum.HOUSE_SEARCH:
      return 'houseSearch'
    default:
      return 'default'
  }
}

export const getBudgetMaxLimit = (travelType: TravelTypeEnum) =>
  travelType === TravelTypeEnum.NIGHTLY_TRIP ? 500 : 250

export const validateBudget = (
  travelType: TravelTypeEnum,
  budget?: Inputs['budget'],
): Inputs['budget'] => {
  const limitBudgetMax = getBudgetMaxLimit(travelType)
  let budgetMin = budget?.budgetMin || 0
  let budgetMax = budget?.budgetMax || 0

  if (budgetMin >= limitBudgetMax) {
    budgetMin = 0
  }
  if (budgetMax >= limitBudgetMax) {
    budgetMax = limitBudgetMax
  }

  if (!budgetMin && !budgetMax) {
    return
  }

  return {
    budgetMax,
    budgetMin,
  }
}

export const setDefaultValues = (data?: Record<string, any>) => {
  if (!data) {
    return undefined
  }
  return {
    ...data,
    budget: validateBudget(data?.travelType, data?.budget),
  }
}

export const shouldRefreshBudgetValue = (
  currentBudget: Inputs['budget'],
  newBudget: Inputs['budget'],
) => {
  if (!currentBudget || !newBudget) {
    return false
  }

  return !isEqual(currentBudget, newBudget)
}

export const formatDates = (
  dateFormatter: DateFormatter,
  dates?: { flexible?: boolean; endDate?: Date; startDate?: Date },
): string => {
  if (!dates) {
    return ''
  }

  if (dates?.flexible) {
    return `Flexible | ${capitalizeFirstLetter(dateFormatter.format({ date: dates?.startDate, formatString: 'MonthYear' }))} `
  }

  return dateFormatter.formatDateRange({
    startDate: dates?.startDate,
    endDate: dates?.endDate,
  })
}

export const formatBudgetOptions = ({
  budget,
  intl,
  travelType,
}: {
  budget?: Inputs['budget']
  travelType: TravelTypeEnum
  intl: IntlShape
}): string => {
  const budgetLimit = getBudgetMaxLimit(travelType)
  const formatter = getFormatter({})
  if (!budget) {
    return ''
  }

  const budgets = budgetMap.get(travelType)
  if (!budgets) {
    return ''
  }

  const budgetType = Object.entries(budgets).find(([_key, value]) =>
    isEqual(
      {
        budgetMax: value.budgetMax,
        budgetMin: value.budgetMin,
      },
      {
        budgetMax: budget.budgetMax,
        budgetMin: budget.budgetMin,
      },
    ),
  )
  if (!budgetType) {
    return ''
  }
  const budgetKey = budgetType[0] === 'Economy' ? 'Economique' : budgetType[0]
  const budgetString = intl.formatMessage(
    {
      id: 'ui.BudgetItem.price',
      defaultMessage:
        '{useMax, select, true {{min} à {max}} other {{min} et plus} }',
    },
    {
      useMax: budget.budgetMax < budgetLimit,
      min: formatter.format(budget.budgetMin),
      max: formatter.format(budget.budgetMax),
    },
  )

  return `${budgetKey} : ${budgetString}`
}
